"use client";

import type { Flashcard } from "@/hooks/useFlashcards";
import { getSavedFlashcardBack, getSavedFlashcardFront } from "@/lib/sessionVocabulary";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Heart, RotateCcw, Trash2, Volume2, X } from "lucide-react";
import * as React from "react";

interface FlashcardReviewCardProps {
  flashcard: Flashcard;
  onClose: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  onToggleFavorite: (id: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  currentIndex: number;
  totalCount: number;
}

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="border-t border-white/10 py-4 first:border-t-0 first:pt-0">
      <h3 className="mb-2 text-xs font-bold text-orange-300">{title}</h3>
      {children}
    </section>
  );
}

export function FlashcardReviewCard({
  flashcard,
  onClose,
  onNext,
  onPrevious,
  onToggleFavorite,
  onDelete,
  currentIndex,
  totalCount,
}: FlashcardReviewCardProps) {
  const [isFlipped, setIsFlipped] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [favoriting, setFavoriting] = React.useState(false);
  const closeButtonRef = React.useRef<HTMLButtonElement>(null);
  const frontText = getSavedFlashcardFront(flashcard);
  const backText = getSavedFlashcardBack(flashcard);
  const isImported = flashcard.card_type === "imported";

  React.useEffect(() => {
    setIsFlipped(false);
  }, [flashcard.id]);

  React.useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus();
    };
  }, []);

  const handleSpeak = () => {
    if (!("speechSynthesis" in window)) return;

    const utterance = new SpeechSynthesisUtterance(frontText);
    const language = flashcard.target_language.toLowerCase();
    utterance.lang = language.includes("spanish")
      ? "es-ES"
      : language.includes("french")
        ? "fr-FR"
        : language.includes("german")
          ? "de-DE"
          : language.includes("italian")
            ? "it-IT"
            : language.includes("dutch")
              ? "nl-NL"
              : language.includes("portuguese")
                ? "pt-PT"
                : language.includes("russian")
                  ? "ru-RU"
                  : language.includes("japanese")
                    ? "ja-JP"
                    : language.includes("chinese")
                      ? "zh-CN"
                      : "en-US";

    speechSynthesis.speak(utterance);
  };

  const handleToggleFavorite = async () => {
    if (favoriting) return;
    try {
      setFavoriting(true);
      await onToggleFavorite(flashcard.id);
    } finally {
      setFavoriting(false);
    }
  };

  const handleDelete = async () => {
    if (deleting || !confirm("Delete this flashcard?")) return;
    try {
      setDeleting(true);
      await onDelete(flashcard.id);
      onClose();
    } finally {
      setDeleting(false);
    }
  };

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      } else if (event.key === "ArrowRight" && onNext) {
        onNext();
      } else if (event.key === "ArrowLeft" && onPrevious) {
        onPrevious();
      } else if (
        (event.key === " " || event.key === "Enter") &&
        !(event.target as HTMLElement).closest("button, a, input, select, textarea")
      ) {
        event.preventDefault();
        setIsFlipped((current) => !current);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, onNext, onPrevious]);

  const flipCard = () => setIsFlipped((current) => !current);

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/[0.85] sm:p-5">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 12 }}
        transition={{ duration: 0.18 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="flashcard-review-title"
        className="flex h-[100dvh] w-full flex-col overflow-hidden bg-[#0f0f14] sm:h-[min(46rem,calc(100dvh-2.5rem))] sm:max-w-2xl sm:rounded-lg sm:border sm:border-white/10 sm:shadow-2xl sm:shadow-black/60"
      >
        <header className="relative z-10 flex h-14 shrink-0 items-center justify-between gap-3 border-b border-white/10 bg-[#0f0f14] px-3 sm:px-4">
          <div className="min-w-0">
            <h2 id="flashcard-review-title" className="truncate text-sm font-bold text-white">
              {flashcard.target_language}
            </h2>
            <p className="text-xs text-white/40">
              Card {currentIndex + 1} of {totalCount}
              {isImported ? " / Imported" : ""}
            </p>
          </div>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handleSpeak}
              className="flex h-9 w-9 items-center justify-center rounded-md text-orange-300 transition-colors hover:bg-orange-400/10 hover:text-orange-200"
              aria-label="Hear pronunciation"
              title="Hear pronunciation"
            >
              <Volume2 size={18} />
            </button>
            <button
              type="button"
              onClick={handleToggleFavorite}
              disabled={favoriting}
              className={`flex h-9 w-9 items-center justify-center rounded-md transition-colors hover:bg-orange-400/10 disabled:opacity-50 ${
                flashcard.is_favorite ? "text-orange-400" : "text-white/45 hover:text-orange-300"
              }`}
              aria-label={flashcard.is_favorite ? "Remove from favorites" : "Add to favorites"}
              title={flashcard.is_favorite ? "Remove from favorites" : "Add to favorites"}
            >
              <Heart size={18} className={flashcard.is_favorite ? "fill-current" : ""} />
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className="flex h-9 w-9 items-center justify-center rounded-md text-white/40 transition-colors hover:bg-red-400/10 hover:text-red-300 disabled:opacity-50"
              aria-label="Delete flashcard"
              title="Delete flashcard"
            >
              <Trash2 size={17} />
            </button>
            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 items-center justify-center rounded-md text-white/55 transition-colors hover:bg-white/[0.06] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/70"
              aria-label="Close review"
              title="Close"
            >
              <X size={19} />
            </button>
          </div>
        </header>

        <div className="relative min-h-0 flex-1 overflow-hidden bg-[#111116]">
          <div className="relative h-full w-full">
            <div
              role="button"
              tabIndex={isFlipped ? -1 : 0}
              aria-hidden={isFlipped}
              aria-label="Reveal answer"
              onClick={flipCard}
              className={`${isFlipped ? "hidden" : "flex"} absolute inset-0 cursor-pointer flex-col bg-[#111116] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-orange-400/70`}
            >
              <div className="flex flex-1 items-center justify-center px-6 py-10 sm:px-10">
                <div className="max-w-xl text-center">
                  <p className="text-xs font-bold text-orange-300">
                    {isImported ? "Front" : "Prompt"}
                  </p>
                  <p className="mt-5 text-2xl font-semibold leading-relaxed text-white sm:text-3xl">
                    {frontText}
                  </p>
                </div>
              </div>
              <div className="flex h-14 shrink-0 items-center justify-center gap-2 border-t border-white/10 text-sm font-semibold text-orange-300">
                <RotateCcw size={16} />
                Reveal answer
              </div>
            </div>

            <div
              role="button"
              tabIndex={isFlipped ? 0 : -1}
              aria-hidden={!isFlipped}
              aria-label="Show prompt"
              onClick={flipCard}
              className={`${isFlipped ? "flex" : "hidden"} absolute inset-0 cursor-pointer flex-col bg-[#111116] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-orange-400/70`}
            >
              <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 overscroll-contain sm:px-7 sm:py-6">
                {backText ? (
                  <DetailSection title={isImported ? "Back" : "Translation"}>
                    <p className="text-lg leading-relaxed text-white/85">{backText}</p>
                  </DetailSection>
                ) : null}

                {isImported && flashcard.source_deck_name ? (
                  <DetailSection title="Deck">
                    <p className="text-sm text-white/65">{flashcard.source_deck_name}</p>
                  </DetailSection>
                ) : null}

                {!isImported &&
                flashcard.ipa_spelling &&
                Object.keys(flashcard.ipa_spelling).length > 0 ? (
                  <DetailSection title="Pronunciation">
                    <div className="divide-y divide-white/[0.08]">
                      {Object.entries(flashcard.ipa_spelling).map(([word, ipa]) => (
                        <div key={word} className="flex items-baseline justify-between gap-4 py-2">
                          <span className="text-sm font-semibold text-white/80">{word}</span>
                          <span className="font-mono text-xs text-orange-200">[{ipa}]</span>
                        </div>
                      ))}
                    </div>
                  </DetailSection>
                ) : null}

                {!isImported && flashcard.vocabulary?.length ? (
                  <DetailSection title="Vocabulary">
                    <div className="divide-y divide-white/[0.08]">
                      {flashcard.vocabulary.map((item, index) => (
                        <div key={`${item.word}-${index}`} className="py-3 first:pt-0 last:pb-0">
                          <div className="flex flex-wrap items-baseline gap-2">
                            <span className="text-sm font-bold text-orange-200">{item.word}</span>
                            <span className="text-xs text-white/35">{item.partOfSpeech}</span>
                          </div>
                          <p className="mt-1 text-sm leading-relaxed text-white/65">
                            {item.definition}
                          </p>
                        </div>
                      ))}
                    </div>
                  </DetailSection>
                ) : null}

                {!isImported && flashcard.grammar_points?.length ? (
                  <DetailSection title="Grammar">
                    <div className="divide-y divide-white/[0.08]">
                      {flashcard.grammar_points.map((point, index) => (
                        <div key={`${point.title}-${index}`} className="py-3 first:pt-0 last:pb-0">
                          <p className="text-sm font-semibold text-white/80">{point.title}</p>
                          <p className="mt-1 text-sm leading-relaxed text-white/60">
                            {point.explanation}
                          </p>
                        </div>
                      ))}
                    </div>
                  </DetailSection>
                ) : null}

                {!isImported && flashcard.example_sentences?.length ? (
                  <DetailSection title="Examples">
                    <div className="divide-y divide-white/[0.08]">
                      {flashcard.example_sentences.map((example, index) => (
                        <div
                          key={`${example.sentence}-${index}`}
                          className="py-3 first:pt-0 last:pb-0"
                        >
                          <p className="text-sm leading-relaxed text-white/80">
                            {example.sentence}
                          </p>
                          <p className="mt-1 text-sm leading-relaxed text-white/45">
                            {example.translation}
                          </p>
                        </div>
                      ))}
                    </div>
                  </DetailSection>
                ) : null}
              </div>
              <div className="flex h-14 shrink-0 items-center justify-center gap-2 border-t border-white/10 text-sm font-semibold text-orange-300">
                <RotateCcw size={16} />
                Show prompt
              </div>
            </div>
          </div>
        </div>

        <footer className="relative z-10 grid h-16 shrink-0 grid-cols-[3.5rem_1fr_3.5rem] items-center border-t border-white/10 bg-[#0f0f14] px-2 pb-[env(safe-area-inset-bottom)] sm:h-14 sm:pb-0">
          <button
            type="button"
            onClick={onPrevious}
            disabled={!onPrevious}
            className="flex h-10 w-10 items-center justify-center rounded-md text-white/65 transition-colors hover:bg-white/[0.06] hover:text-white disabled:cursor-not-allowed disabled:text-white/15"
            aria-label="Previous flashcard"
            title="Previous"
          >
            <ChevronLeft size={22} />
          </button>
          <span className="text-center text-xs font-semibold text-white/35">
            {currentIndex + 1} / {totalCount}
          </span>
          <button
            type="button"
            onClick={onNext}
            disabled={!onNext}
            className="ml-auto flex h-10 w-10 items-center justify-center rounded-md text-white/65 transition-colors hover:bg-white/[0.06] hover:text-white disabled:cursor-not-allowed disabled:text-white/15"
            aria-label="Next flashcard"
            title="Next"
          >
            <ChevronRight size={22} />
          </button>
        </footer>
      </motion.div>
    </div>
  );
}
